/**
 * Generic CRM entity trigger card variant — for entity types without a
 * dedicated renderer. Header + collapsed PreviewBody only (no expanded
 * body: `getGenericPreviewFields`, in `./helpers`, is also what the parent
 * uses to decide whether the card is expandable at all).
 */

import type { CrmEventTriggerData } from '@/lib/proposals/core/types';

import { getEntityTypeLabel } from '../shared';

/**
 * Generic CRM event header for entity types without a dedicated renderer.
 * Mirrors the visual rhythm of `MeetingNotesHeader` / `ResearchHeader` —
 * display name as the title, entity-type label after a middot — so the
 * trigger card never collapses into a thin pill-only row.
 */
export function GenericCrmHeader({
  data,
  entityTypeName,
}: {
  data: CrmEventTriggerData;
  entityTypeName: string;
}) {
  const { display_name } = data.crm_event;
  const typeLabel = getEntityTypeLabel(entityTypeName);
  return (
    <div className='flex-1 min-w-0'>
      <div className='flex items-center gap-2'>
        <span className='text-sm font-medium text-primary truncate'>
          {display_name}
        </span>
        {typeLabel && (
          <>
            <span className='text-tertiary flex-shrink-0'>·</span>
            <span className='text-xs text-tertiary flex-shrink-0'>
              {typeLabel}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/** Compact generic preview — one line of secondary identifiers (e.g.
 *  "Ambassador · hannah@primamente.com" for a contact). Caller is
 *  responsible for only rendering this when `fields` is non-empty so the
 *  parent's `isExpandable` decision matches what's actually visible. */
export function GenericCrmPreviewBody({ fields }: { fields: string[] }) {
  return (
    <div className='text-xs text-tertiary truncate'>{fields.join(' · ')}</div>
  );
}
